// Touch/pen/mouse gestures for messages and chat rows (web and the app):
//  - horizontal swipe (left / right) with the element following the finger,
//    an action revealed underneath and a small haptic tick at the threshold;
//    vertical movement is left to scrolling
//  - long-press (touch) to open the context menu; right-click does the same
// Pointer events cover touch, pen and mouse alike; `touch-action: pan-y` on
// the element (see styles) keeps vertical scrolling native.
import { useEffect, useRef } from "preact/hooks";

export interface GestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  /** touch: a held finger (vs a right-click) */
  onLongPress?: (touch: boolean) => void;
  /** Mouse drags also swipe (off for text you might want to select). */
  mouseSwipe?: boolean;
  threshold?: number; // px to trigger
  enabled?: boolean;
}

const LOCK = 10; // px before deciding horizontal vs vertical
const LONG_MS = 450;

const tick = () => {
  try {
    navigator.vibrate?.(12);
  } catch {
    /* not supported */
  }
};

/**
 * Returns handlers for the element that should react (usually the row) and a
 * ref for the element that moves (the bubble / the row's content). The moving
 * element gets `--swipe` (px) and the row `data-swipe="left|right"` + a
 * `data-armed` flag once past the threshold, for CSS.
 */
export function useGestures(opts: GestureOptions) {
  const target = useRef<HTMLElement | null>(null);
  const o = useRef(opts);
  o.current = opts;
  const state = useRef<{
    id: number;
    x: number;
    y: number;
    dx: number;
    mode: "idle" | "pending" | "swipe" | "scroll";
    row: HTMLElement | null;
    armed: boolean;
    long?: number;
    fired?: boolean;
    quietUntil: number; // swallow the click that ends a swipe (this element only)
  }>({ id: -1, x: 0, y: 0, dx: 0, mode: "idle", row: null, armed: false, quietUntil: 0 });

  const set = (dx: number) => {
    const s = state.current;
    s.dx = dx;
    const el = target.current;
    if (el) el.style.setProperty("--swipe", dx + "px");
    const row = s.row;
    if (!row) return;
    const th = o.current.threshold ?? 64;
    const armed = Math.abs(dx) >= th;
    if (armed !== s.armed) {
      s.armed = armed;
      if (armed) tick();
    }
    if (dx === 0) {
      delete row.dataset.swipe;
      delete row.dataset.armed;
    } else {
      row.dataset.swipe = dx > 0 ? "right" : "left";
      if (armed) row.dataset.armed = "1";
      else delete row.dataset.armed;
    }
  };

  const reset = (animate: boolean) => {
    const el = target.current;
    const row = state.current.row;
    state.current.mode = "idle";
    state.current.armed = false;
    if (el && row && animate) {
      // Slide back, then drop the swipe state (and with it the transform).
      el.style.transition = "transform 0.18s ease-out";
      el.style.setProperty("--swipe", "0px");
      delete row.dataset.armed;
      window.setTimeout(() => {
        el.style.transition = "";
        if (state.current.mode !== "swipe") set(0);
      }, 200);
      return;
    }
    set(0);
  };

  const onMove = (e: PointerEvent) => {
    const s = state.current;
    if (e.pointerId !== s.id) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.mode === "pending") {
      if (Math.abs(dx) < LOCK && Math.abs(dy) < LOCK) return;
      clearTimeout(s.long); // moved: not a long-press
      const horizontal = Math.abs(dx) > Math.abs(dy) * 1.4;
      const can = (dx > 0 && o.current.onSwipeRight) || (dx < 0 && o.current.onSwipeLeft);
      if (!horizontal || !can || (e.pointerType === "mouse" && !o.current.mouseSwipe)) {
        s.mode = "scroll";
        return;
      }
      s.mode = "swipe";
      s.x += Math.sign(dx) * LOCK; // start from the lock point: no jump
      window.getSelection()?.removeAllRanges();
    }
    if (s.mode !== "swipe") return;
    e.preventDefault();
    let d = e.clientX - s.x;
    if (d > 0 && !o.current.onSwipeRight) d = 0;
    if (d < 0 && !o.current.onSwipeLeft) d = 0;
    const max = 110;
    // Rubber band past the max.
    const sign = Math.sign(d);
    const a = Math.abs(d);
    set(sign * (a > max ? max + (a - max) * 0.2 : a));
  };

  const onUp = (e: PointerEvent) => {
    const s = state.current;
    if (e.pointerId !== s.id) return;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    clearTimeout(s.long);
    if (s.mode === "swipe") {
      const armed = s.armed;
      const dir = s.dx > 0 ? "right" : "left";
      reset(true);
      s.quietUntil = Date.now() + 400; // the click this pointerup produces isn't a tap
      if (armed) (dir === "right" ? o.current.onSwipeRight : o.current.onSwipeLeft)?.();
    } else {
      s.mode = "idle";
    }
  };

  const onPointerDown = (e: PointerEvent) => {
    if (o.current.enabled === false || e.button !== 0) return;
    // Buttons, links and inputs inside keep their own behaviour.
    if ((e.target as HTMLElement).closest("button, a, input, textarea, select, audio, video, .no-gesture")) return;
    const s = state.current;
    s.id = e.pointerId;
    s.x = e.clientX;
    s.y = e.clientY;
    s.mode = "pending";
    s.fired = false;
    s.row = e.currentTarget as HTMLElement;
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    if (e.pointerType !== "mouse" && o.current.onLongPress) {
      clearTimeout(s.long);
      s.long = window.setTimeout(() => {
        if (s.mode !== "pending") return;
        s.mode = "idle";
        s.fired = true;
        tick();
        o.current.onLongPress?.(true);
      }, LONG_MS);
    }
  };

  const onContextMenu = (e: MouseEvent) => {
    if (!o.current.onLongPress || o.current.enabled === false) return;
    if ((e.target as HTMLElement).closest("a, input, textarea, img, video")) return; // keep native menus there
    e.preventDefault();
    const s = state.current;
    clearTimeout(s.long);
    if (s.fired) return; // the long-press already opened it
    o.current.onLongPress(false);
  };

  useEffect(
    () => () => {
      clearTimeout(state.current.long);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    },
    [],
  );

  const onClickCapture = (e: MouseEvent) => {
    const s = state.current;
    if (Date.now() < s.quietUntil || s.fired) {
      s.quietUntil = 0;
      s.fired = false;
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return { bind: { onPointerDown, onContextMenu, onClickCapture }, target };
}
